/**
 * A subject's claims in time order.
 *
 * Reached through the evidence attached to them, because a research subject deliberately
 * has no case — that is the point of `research_subject`. A rejected attribution leaves the
 * timeline with it: showing a researcher a history they have already said is not their
 * subject's would be worse than showing none.
 *
 * **An undated claim is not dropped.** A source that gave no validity period still
 * observed something at a known moment, so the item sits at its observation time and says
 * so. Dropping it would make the timeline quietly incomplete, which is the failure this
 * product exists to avoid.
 */

import type { ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { request } from "@/lib/api";
import { Badge, EmptyState, ErrorState, Loading } from "@/components/ui";

interface TimelineItem {
  assertion_id: string;
  predicate: string;
  object_type: string;
  object_value: string;
  observed_at: string;
  valid_from: string | null;
  valid_to: string | null;
  has_asserted_period: boolean;
  source_reliability: string;
  extraction_confidence: number;
  information_credibility: number;
  source: string;
  evidence_sha256: string;
  is_special_category: boolean;
}

/**
 * The timeline panel.
 *
 * @param props - The subject to read.
 * @returns The panel.
 */
export function SubjectTimelinePanel({ subjectId }: { subjectId: string }): ReactElement {
  const timeline = useQuery({
    queryKey: ["subject-timeline", subjectId],
    queryFn: () => request<TimelineItem[]>(`/research/subjects/${subjectId}/timeline`),
  });

  if (timeline.isLoading) return <Loading label="Loading timeline" />;
  if (timeline.error != null) return <ErrorState error={timeline.error} />;

  const items = timeline.data ?? [];

  return (
    <section className="space-y-3" aria-label="Timeline panel" data-testid="subject-panel-timeline">
      <p className="max-w-2xl text-xs text-muted">
        Every claim on the evidence attached to this subject, in the order it is asserted to
        have happened. A claim its source did not date sits at the moment it was observed,
        and is marked as undated rather than quietly given a date it never had.
      </p>

      {items.length === 0 ? (
        <EmptyState>Nothing on the timeline yet. It fills as evidence is attached.</EmptyState>
      ) : (
        <ol className="space-y-2" data-testid="timeline-list">
          {items.map((item) => (
            <li
              key={item.assertion_id}
              className="space-y-1 rounded border border-line p-3"
              data-testid="timeline-item"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold">
                  {(item.valid_from ?? item.observed_at).slice(0, 10)}
                </span>
                {!item.has_asserted_period && <Badge tone="muted">undated by its source</Badge>}
                {item.is_special_category && <Badge tone="warn">special category</Badge>}
                <span className="font-mono text-xs text-muted">{item.source}</span>
                <Badge tone="muted">credibility {item.information_credibility}/6</Badge>
              </div>
              <p className="text-sm">
                <span className="text-muted">{item.predicate.replaceAll("_", " ")}</span>{" "}
                {item.object_value}
              </p>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
